import type { OrderLine, OrderStep } from '../../shared/orderStatus';

/**
 * How a step should be presented, derived from BC's milestone statuses.
 *
 * BC treats steps as milestones: "completed" means the milestone was reached
 * and "active" means the next milestone BC is waiting on. So where a line is
 * right now is its highest completed step, and BC's "active" step is what
 * comes next. BC's `currentStep` field names the active step, so it is not
 * used for display.
 */
export type StepRole = 'done' | 'current' | 'next' | 'upcoming';

export interface LineProgress {
  steps: OrderStep[];
  /** Index of the step the line is on now, or -1 when nothing is reached yet. */
  currentIndex: number;
  /** Index of the step BC is waiting on, or -1 when every step is reached. */
  nextIndex: number;
  allDone: boolean;
  /** Share of milestones reached, 0 to 100. */
  percent: number;
}

const QUEUED_STEP_NAME = 'Queued for Repair';

/**
 * Interim shim until CBMBC-API ships its own "Queued for Repair" step.
 *
 * BC currently completes "In Progress" when the assembly stage is Dispatched,
 * which at CBM means the unit is received and waiting for a technician. This
 * rewrites that case into the planned step shape: "Queued for Repair" reached,
 * "In Progress" up next. It does nothing once BC sends the queued step itself.
 */
function normalizeDispatchedRepair(line: OrderLine): OrderStep[] {
  const steps = line.steps ?? [];
  if (line.itemSaleType !== 'Repair') return steps;
  if (steps.some((s) => s.name === QUEUED_STEP_NAME)) return steps;

  const inProgressIndex = steps.findIndex((s) => s.name === 'In Progress');
  if (inProgressIndex < 0) return steps;

  const inProgress = steps[inProgressIndex];
  if (inProgress.status !== 'completed' || inProgress.details?.assemblyStage !== 'Dispatched') {
    return steps;
  }
  // Leave anything unusual alone, such as a later step with its own evidence.
  if (steps.slice(inProgressIndex + 1).some((s) => s.status === 'completed')) return steps;

  const queued: OrderStep = {
    sequence: 0,
    name: QUEUED_STEP_NAME,
    status: 'completed',
    completedAt: inProgress.completedAt,
  };
  const waitingForWork: OrderStep = { sequence: 0, name: inProgress.name, status: 'active' };
  const later = steps
    .slice(inProgressIndex + 1)
    .map((s): OrderStep => ({ ...s, status: 'pending' }));

  return [...steps.slice(0, inProgressIndex), queued, waitingForWork, ...later].map((s, i) => ({
    ...s,
    sequence: i + 1,
  }));
}

export function getLineProgress(line: OrderLine): LineProgress {
  const steps = normalizeDispatchedRepair(line);

  let currentIndex = -1;
  steps.forEach((s, i) => {
    if (s.status === 'completed') currentIndex = i;
  });

  const allDone = steps.length > 0 && steps.every((s) => s.status === 'completed');
  const nextIndex = allDone || currentIndex + 1 >= steps.length ? -1 : currentIndex + 1;
  const percent = steps.length === 0 ? 0 : ((currentIndex + 1) / steps.length) * 100;

  return { steps, currentIndex, nextIndex, allDone, percent };
}

export function stepRole(progress: LineProgress, index: number): StepRole {
  if (progress.allDone || index < progress.currentIndex) return 'done';
  if (index === progress.currentIndex) return 'current';
  if (index === progress.nextIndex) return 'next';
  return 'upcoming';
}
